// One automation: dry run switch, leads inside (stop one), steps, last runs.

import { stageLabel, type Stage } from '@ouradent/shared';
import {
  setAutomation,
  stopLeadAutomation,
  useAutomations,
  useBoard,
  type AutoRun,
} from '../../lib/data';
import { fShort } from '../../lib/format';
import { CloseX, useDrawer, useToast } from '../../ui/kit';

const RUN_TEXT: Record<AutoRun['status'], string> = {
  pending: 'Waiting',
  running: 'Running',
  sent: 'Done',
  skipped: 'Skipped',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export function AutomationDrawer({ autoKey }: { autoKey: string }) {
  const q = useAutomations();
  const board = useBoard();
  const toast = useToast();
  const { open } = useDrawer();
  const a = q.data?.autos.find((x) => x.key === autoKey);
  if (!q.data || !a) return null;
  const leads = new Map((board.data ?? []).map((l) => [l.id, l]));
  const inside = q.data.enrolled.filter((e) => e.automation_id === a.id);
  const runs = q.data.runs
    .filter((r) => r.automation_id === a.id && r.status !== 'pending')
    .slice(0, 25);
  const waiting = q.data.runs
    .filter((r) => r.automation_id === a.id && r.status === 'pending')
    .sort((x, y) => x.due_at.localeCompare(y.due_at))
    .slice(0, 10);

  async function run(p: Promise<unknown>, done: string) {
    try {
      await p;
      toast(done);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }

  const stops = a.definition.stop_unless_stage;

  return (
    <>
      <div className="dhead">
        <div>
          <h2 id="dtitle">{a.name}</h2>
          <div className="sm">When: {a.trigger_text}</div>
        </div>
        <CloseX />
      </div>
      <div className="dbody">
        <div className="blk">
          <div
            className="panel"
            style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <div style={{ flex: 1, minWidth: 200 }}>
              <b>{!a.enabled ? 'Off' : a.dry_run ? 'On, dry run' : 'On, live'}</b>
              <div style={{ color: 'var(--muted)', fontSize: 14 }}>
                {a.dry_run
                  ? 'Logs what each step would do. Nothing is created.'
                  : 'Creates tasks, next steps and notifications for real.'}
              </div>
            </div>
            <button
              className={`btn ${a.dry_run ? 'primary' : ''}`}
              onClick={() =>
                void run(
                  setAutomation(a.key, true, !a.dry_run),
                  a.dry_run ? `${a.name} is live` : `${a.name} back in dry run`,
                )
              }
            >
              {a.dry_run ? 'Go live' : 'Back to dry run'}
            </button>
          </div>
        </div>

        <div className="blk">
          <h3>
            <span className="lbl g">Steps</span>
          </h3>
          <div className="panel">
            <div className="vsteps">
              {a.step_labels.map((s) => (
                <div key={s}>{s}</div>
              ))}
            </div>
          </div>
        </div>

        {stops && (
          <div className="blk">
            <h3>
              <span className="lbl g">Stops when</span>
            </h3>
            <div className="panel">
              The lead leaves {stops.map((s) => stageLabel(s as Stage)).join(' or ')}, or someone
              stops it here.
            </div>
          </div>
        )}

        <div className="blk">
          <h3>
            <span className="lbl g">Leads inside · {inside.length}</span>
          </h3>
          <div className="panel">
            {inside.length ? (
              inside.map((e) => (
                <div key={e.id} className="minirow">
                  <div className="grow">
                    <b>{leads.get(e.lead_id)?.clinic ?? 'Lead'}</b>
                    <div style={{ color: 'var(--muted)', fontSize: 14 }}>
                      Since {fShort(e.enrolled_at)}
                    </div>
                  </div>
                  <button className="btn sm" onClick={() => open({ type: 'lead', id: e.lead_id })}>
                    Open
                  </button>
                  <button
                    className="btn sm ghost"
                    onClick={() => void run(stopLeadAutomation(e.id), 'Stopped for this lead')}
                  >
                    Stop
                  </button>
                </div>
              ))
            ) : (
              <div className="minirow" style={{ color: 'var(--muted)' }}>
                No lead inside right now.
              </div>
            )}
          </div>
        </div>

        {waiting.length > 0 && (
          <div className="blk">
            <h3>
              <span className="lbl g">Coming up</span>
            </h3>
            <div className="panel">
              {waiting.map((r) => (
                <div key={r.id} className="minirow">
                  <div className="grow">
                    {r.kind === 'ghl_tag' ? 'GHL tag' : r.step_key} ·{' '}
                    {leads.get(r.lead_id)?.clinic ?? 'Lead'}
                  </div>
                  <span style={{ color: 'var(--muted)', fontSize: 14 }}>{fShort(r.due_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="blk">
          <h3>
            <span className="lbl g">Last runs</span>
          </h3>
          <div className="panel">
            {runs.length ? (
              runs.map((r) => (
                <div key={r.id} className="minirow">
                  <span
                    className={`sdot ${r.status === 'failed' ? 'err' : r.status === 'sent' ? 'ok' : 'warn'}`}
                    aria-hidden
                  />
                  <div className="grow">
                    {r.result?.would ?? r.result?.did ?? r.result?.reason ?? r.error ?? r.step_key}
                    <div style={{ color: 'var(--muted)', fontSize: 14 }}>
                      {r.result?.dry_run ? 'Dry run' : RUN_TEXT[r.status]} ·{' '}
                      {leads.get(r.lead_id)?.clinic ?? 'Lead'}
                    </div>
                  </div>
                  <span style={{ color: 'var(--muted)', fontSize: 14 }}>
                    {fShort(r.completed_at ?? r.due_at)}
                  </span>
                </div>
              ))
            ) : (
              <div className="minirow" style={{ color: 'var(--muted)' }}>
                Nothing has run yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
